import { CustomThemeMobileBackgroundMode, DesignLayoutType } from '@graphql/generated-base';
import type { CustomArtTheme, ResolveCustomArtInput, ResolvedCustomArt } from './resolveCustomArt.types';

// Split View is unchanged by the flag (D13); no mobile upload means theme art, not desktop.
const resolveSplitView = (customTheme: CustomArtTheme | null | undefined): ResolvedCustomArt => ({
  desktopBackground: customTheme?.desktopThemeAsset ?? null,
  mobileBackground: customTheme?.mobileThemeAsset ?? null,
  mobileInheritsDesktop: false,
  bannerEnabled: false,
  bannerImage: null,
});

// Today's Classic Navigation: body art is always desktop; the mobile upload is only the banner.
const resolveLegacyClassic = (customTheme: CustomArtTheme | null | undefined): ResolvedCustomArt => {
  const desktopBackground = customTheme?.desktopThemeAsset ?? null;
  const banner = customTheme?.mobileThemeAsset ?? null;
  return {
    desktopBackground,
    mobileBackground: desktopBackground,
    mobileInheritsDesktop: !!desktopBackground,
    bannerEnabled: !!banner,
    bannerImage: banner,
  };
};

// Slots are independent: nothing borrows another slot's image. Null fields keep today's rules,
// so untouched events match resolveLegacyClassic.
const resolveClassic = (customTheme: CustomArtTheme | null | undefined): ResolvedCustomArt => {
  const desktopBackground = customTheme?.desktopThemeAsset ?? null;
  const mode = customTheme?.mobileBackgroundMode ?? CustomThemeMobileBackgroundMode.INHERIT;
  const mobileBackground =
    mode === CustomThemeMobileBackgroundMode.OWN ? (customTheme?.mobileBackgroundAsset ?? null) : mode === CustomThemeMobileBackgroundMode.NONE ? null : desktopBackground;
  const bannerAsset = customTheme?.mobileThemeAsset ?? null;
  const bannerEnabled = customTheme?.mobileBannerEnabled ?? !!bannerAsset;
  return {
    desktopBackground,
    mobileBackground,
    mobileInheritsDesktop: mode === CustomThemeMobileBackgroundMode.INHERIT && !!desktopBackground,
    bannerEnabled,
    bannerImage: bannerEnabled ? bannerAsset : null,
  };
};

/**
 * Picks the asset for desktop body art, mobile body art and the Classic Navigation banner.
 * Pure (flag passed in) so admin, guest and SSR share it. Flag off ⇒ today's output.
 */
export const resolveCustomArt = ({ layout, customTheme, isNewCustomArtPanelEnabled }: ResolveCustomArtInput): ResolvedCustomArt => {
  if (layout === DesignLayoutType.aloha) {
    return resolveSplitView(customTheme);
  }
  return isNewCustomArtPanelEnabled ? resolveClassic(customTheme) : resolveLegacyClassic(customTheme);
};
